/**
 * blog_edit_controller.js — admin blog editor page glue (Stimulus).
 *
 * Page-level behaviour for templates/admin/blog/edit.html that doesn't belong
 * to the Tiptap editor itself (that's blog_editor_controller.js): slug
 * auto-fill from the title while the admin hasn't touched the slug field yet,
 * and a debounced autosave of the whole form for existing posts.
 *
 * Autosave polls on a fixed interval (rather than binding to Tiptap's onUpdate)
 * because the hidden body_json/body_html inputs are populated by
 * editor.getJSON()/getHTML() via direct .value assignment, which does not
 * fire a native "input" event a form-level listener could catch.
 *
 * Targets: form, title, slug (only present when editing an existing post),
 *          saveStatus, ogImageUrl, scheduledAt (the schedule form's
 *          datetime-local input, defaulted + driven by presetSchedule()).
 * Values: autosaveUrl (empty string on the "New Post" page — new posts have
 *         nowhere to autosave to until the first explicit "Save draft"),
 *         uploadUrl (the shared /admin/blog/upload-image endpoint).
 */
import { Controller } from "@hotwired/stimulus";

const AUTOSAVE_INTERVAL_MS = 15000;

function slugify(text) {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

// datetime-local has no timezone of its own -- whatever digits are in the
// field are posted verbatim and interpreted as UTC server-side (the button
// is labelled "Schedule (UTC)"), so presets must be built from the UTC
// getters/setters, not local-time equivalents.
function formatUtcForInput(date) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
}

function presetDate(preset) {
    const target = new Date();
    if (preset === "1h") {
        target.setUTCMinutes(target.getUTCMinutes() + 60);
        target.setUTCMinutes(Math.ceil(target.getUTCMinutes() / 15) * 15, 0, 0);
        return target;
    }
    if (preset === "monday-9am") {
        const daysUntilMonday = ((1 - target.getUTCDay() + 7) % 7) || 7;
        target.setUTCDate(target.getUTCDate() + daysUntilMonday);
        target.setUTCHours(9, 0, 0, 0);
        return target;
    }
    // "tomorrow-9am" default
    target.setUTCDate(target.getUTCDate() + 1);
    target.setUTCHours(9, 0, 0, 0);
    return target;
}

export default class extends Controller {
    static targets = ["form", "title", "slug", "saveStatus", "ogImageUrl", "scheduledAt"];
    static values = { autosaveUrl: String, uploadUrl: String };

    connect() {
        this._slugAuto = !this.hasSlugTarget || !this.slugTarget.value;
        if (this.hasSlugTarget) {
            this._onSlugInput = () => { this._slugAuto = false; };
            this.slugTarget.addEventListener("input", this._onSlugInput);
        }

        if (this.hasScheduledAtTarget && !this.scheduledAtTarget.value) {
            this.scheduledAtTarget.value = formatUtcForInput(presetDate("tomorrow-9am"));
        }

        if (this.autosaveUrlValue) {
            this._lastSerialized = this._serializeForm();
            this._autosaveTimer = setInterval(() => this._autosave(), AUTOSAVE_INTERVAL_MS);
        }
    }

    // Fired by the schedule preset buttons
    // (data-action="blog-edit#presetSchedule" data-preset="...").
    presetSchedule(event) {
        if (!this.hasScheduledAtTarget) return;
        this.scheduledAtTarget.value = formatUtcForInput(presetDate(event.currentTarget.dataset.preset));
    }

    disconnect() {
        if (this._autosaveTimer) clearInterval(this._autosaveTimer);
        if (this.hasSlugTarget && this._onSlugInput) {
            this.slugTarget.removeEventListener("input", this._onSlugInput);
        }
    }

    syncSlug() {
        if (!this.hasSlugTarget || !this._slugAuto) return;
        this.slugTarget.value = slugify(this.titleTarget.value);
    }

    // Fired by the OG image file input's change event
    // (data-action="change->blog-edit#uploadOgImage").
    async uploadOgImage(event) {
        const file = event.target.files && event.target.files[0];
        event.target.value = ""; // allow re-picking the same file later
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);
        try {
            const res = await fetch(this.uploadUrlValue, { method: "POST", body: formData });
            if (!res.ok) return;
            const data = await res.json();
            if (data.url && this.hasOgImageUrlTarget) this.ogImageUrlTarget.value = data.url;
        } catch (_e) {
            // Silent -- the admin can still paste a URL manually.
        }
    }

    _serializeForm() {
        return new URLSearchParams(new FormData(this.formTarget)).toString();
    }

    async _autosave() {
        const serialized = this._serializeForm();
        if (serialized === this._lastSerialized) return;

        try {
            const res = await fetch(this.autosaveUrlValue, {
                method: "POST",
                headers: { "X-Requested-With": "XMLHttpRequest" },
                body: new FormData(this.formTarget),
            });
            if (!res.ok) throw new Error(`Autosave failed: ${res.status}`);
            this._lastSerialized = serialized;
            if (this.hasSaveStatusTarget) {
                this.saveStatusTarget.textContent = "Saved";
                setTimeout(() => { this.saveStatusTarget.textContent = ""; }, 2000);
            }
        } catch {
            if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Autosave failed";
        }
    }
}
